import { formatDateTime } from "@/shared/lib/date-time";
import { toDateTimeLocalValue } from "@/shared/lib/datetime-local-form";
import { FormDateQuarterTimeField } from "@/shared/ui/FormDateQuarterTimeField";
import { Button, FormRoot, Modal, RHF, zodResolver } from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";

const EditCheckInTimeSchema = z.object({
  checkedInAt: z.string().min(1, "ຕ້ອງລະບຸເວລາ"),
});

type EditCheckInTimeFormValues = z.infer<typeof EditCheckInTimeSchema>;

export type EditCheckInTimeTarget = {
  id: string;
  label: string;
  checkedInAt: string;
};

type EditCheckInTimeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: EditCheckInTimeTarget | null;
  onConfirm: (values: { id: string; checkedInAt: string }) => void | Promise<void>;
  isSubmitting?: boolean;
};

export function EditCheckInTimeDialog({
  open,
  onOpenChange,
  target,
  onConfirm,
  isSubmitting = false,
}: EditCheckInTimeDialogProps) {
  const methods = RHF.useForm<EditCheckInTimeFormValues>({
    resolver: zodResolver(EditCheckInTimeSchema),
    defaultValues: {
      checkedInAt: "",
    },
  });

  useEffect(() => {
    if (open && target) {
      methods.reset({
        checkedInAt: toDateTimeLocalValue(target.checkedInAt),
      });
    }
  }, [open, target, methods]);

  const handleSubmit = async (values: EditCheckInTimeFormValues) => {
    if (!target) return;
    await onConfirm({
      id: target.id,
      checkedInAt: values.checkedInAt,
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) methods.reset();
    onOpenChange(next);
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="ແກ້ໄຂເວລາ check-in"
      size="md"
    >
      <FormRoot
        methods={methods}
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        {target ? (
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p className="font-medium">{target.label}</p>
            <p className="text-muted-foreground text-xs">
              ເວລາປັດຈຸບັນ: {formatDateTime(target.checkedInAt)}
            </p>
          </div>
        ) : null}

        <FormDateQuarterTimeField
          name="checkedInAt"
          label="ເວລາ check-in ໃໝ່"
          requiredMark
          disabled={isSubmitting}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            ຍົກເລີກ
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      </FormRoot>
    </Modal>
  );
}
